/**
 * FAQ セクション。3 variants:
 *   accordion   — 折りたたみアコーディオン (デフォルトで閉じる)
 *   simple_list — フラットリスト (常時表示)
 *   two_column  — 2 段組のフラットリスト
 */
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useState } from 'react';

import { ArrayField, TextField, TextareaField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface FaqItem {
  question: string;
  answer: string;
}
interface FaqConfig {
  title: string;
  subtitle: string;
  items: FaqItem[];
}

function FaqEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as FaqConfig;
  const set = (patch: Partial<FaqConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="セクション見出し"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="サブタイトル"
        value={cfg.subtitle ?? ''}
        onChange={(v) => set({ subtitle: v })}
        onCommit={onCommit}
      />
      <ArrayField<FaqItem>
        label="質問"
        items={cfg.items ?? []}
        onChange={(next) => set({ items: next })}
        emptyItem={() => ({ question: '', answer: '' })}
        renderItem={(item, _i, update) => (
          <div className="space-y-2">
            <TextField
              label="質問"
              value={item.question}
              onChange={(v) => update({ question: v })}
              onCommit={onCommit}
            />
            <TextareaField
              label="回答 (HTML 可)"
              value={item.answer}
              onChange={(v) => update({ answer: v })}
              onCommit={onCommit}
              rows={3}
            />
          </div>
        )}
      />
    </div>
  );
}

function AccordionRenderer({ config }: RendererProps) {
  const v = config as unknown as FaqConfig;
  const items = v.items ?? [];
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-6">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="space-y-2 max-w-3xl mx-auto">
        {items.map((it, i) => {
          const open = openIdx === i;
          return (
            <div
              key={i}
              className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)]"
            >
              <button
                type="button"
                onClick={() => setOpenIdx(open ? null : i)}
                className="w-full p-4 text-left flex items-center justify-between gap-3"
              >
                <span className="font-semibold text-sm">Q. {it.question}</span>
                <ChevronDown
                  className={`size-4 text-[var(--text-muted)] transition-transform print:hidden ${
                    open ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {/* 画面: open のときだけ表示 / 印刷: 常に表示 */}
              <div
                className={`px-4 pb-4 text-sm text-[var(--text-secondary)] prose dark:prose-invert max-w-none prose-sm ${
                  open ? 'block' : 'hidden print:block'
                }`}
                dangerouslySetInnerHTML={{ __html: it.answer ?? '' }}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SimpleListRenderer({ config }: RendererProps) {
  const v = config as unknown as FaqConfig;
  const items = v.items ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="mb-6">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="space-y-5 max-w-3xl">
        {items.map((it, i) => (
          <div key={i}>
            <div className="font-semibold mb-1">Q. {it.question}</div>
            <div
              className="text-sm text-[var(--text-secondary)] prose dark:prose-invert max-w-none prose-sm"
              dangerouslySetInnerHTML={{ __html: it.answer ?? '' }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function TwoColumnRenderer({ config }: RendererProps) {
  const v = config as unknown as FaqConfig;
  const items = v.items ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-6">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {items.map((it, i) => (
          <div key={i}>
            <div className="font-semibold mb-1">Q. {it.question}</div>
            <div
              className="text-sm text-[var(--text-secondary)] prose dark:prose-invert max-w-none prose-sm"
              dangerouslySetInnerHTML={{ __html: it.answer ?? '' }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

const sample = (): Record<string, unknown> => ({
  title: 'よくある質問',
  subtitle: 'お問い合わせ前にご確認ください',
  items: [
    {
      question: '導入までの最短期間は?',
      answer: '<p>標準プランで最短 3 営業日です。要件によって変動します。</p>',
    },
    {
      question: '初期費用はいくら必要ですか?',
      answer:
        '<p>プランにより異なります。詳細は <a href="/pricing">料金ページ</a> をご覧ください。</p>',
    },
    {
      question: '途中でプラン変更できますか?',
      answer: '<p>いつでもアップグレード / ダウングレード可能です。</p>',
    },
  ] as FaqItem[],
});

export const faqDefinition: SectionDefinition = {
  type: 'faq',
  label: 'よくある質問',
  icon: HelpCircle,
  description: 'Q&A 形式で疑問に答える。',
  defaultVariant: 'accordion',
  useCases: ['all'],
  variants: {
    accordion: {
      label: 'アコーディオン',
      description: 'クリックで展開する折りたたみ',
      Renderer: AccordionRenderer,
      Editor: FaqEditor,
      sample,
    },
    simple_list: {
      label: 'シンプルリスト',
      description: '常時表示の縦リスト',
      Renderer: SimpleListRenderer,
      Editor: FaqEditor,
      sample,
    },
    two_column: {
      label: '2段組',
      description: '2 列で並べる',
      Renderer: TwoColumnRenderer,
      Editor: FaqEditor,
      sample,
    },
  },
};
